import React, { useState, useEffect } from 'react';
import { Check, Plus, Minus, BarChart3, Trash2, Loader2 } from 'lucide-react';
import { useProducts } from '../hooks/useApi';

const BASE_KEYS = ['accuracy', 'aperture', 'measurementSpeed', 'category'];

const humanize = (key) => key.replace(/([A-Z])/g, ' $1').trim();

const ProductChips = ({ products, loading, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2 mb-10">
    {loading && (!products || products.length === 0) ? (
      <div className="flex items-center gap-2 text-slate-400 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading products...
      </div>
    ) : (products || []).map((p) => {
      const on = selected.includes(p.id);
      return (
        <button
          key={p.id}
          onClick={() => onToggle(p.id)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-2 btn-transition ${
            on
              ? 'bg-[#0A2A63] text-white border-[#0A2A63]'
              : 'bg-white text-[#0A2A63] border-slate-200 hover:border-[#0A2A63]'
          }`}
        >
          {on ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {p.code}
        </button>
      );
    })}
  </div>
);

const ProductHeaderCell = ({ p, onRemove }) => (
  <div className="p-6 relative border-l border-slate-100">
    <button
      onClick={onRemove}
      className="absolute top-3 right-3 h-7 w-7 rounded-full bg-slate-100 hover:bg-red-100 hover:text-red-600 flex items-center justify-center transition-colors"
      aria-label="Remove"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
    <div className="h-28 bg-slate-50 rounded-xl flex items-center justify-center p-2 mb-4">
      <img src={p.image} alt={p.name} className="max-h-full max-w-full object-contain" />
    </div>
    <p className="text-[10px] uppercase tracking-widest text-[#FF4E50] font-bold">{p.brand}</p>
    <p className="text-sm font-bold text-[#050F2E] mt-1 leading-tight">{p.name}</p>
    <p className="text-lg font-black text-[#0A2A63] mt-3">
      {p.price ? `$${p.price.toLocaleString()}` : p.priceLabel || 'Contact'}
    </p>
  </div>
);

const CompareRow = ({ label, cols, children, capitalize = false, alignStart = false }) => (
  <div
    className={`grid ${alignStart ? 'items-start' : 'items-center'}`}
    style={{ gridTemplateColumns: `140px repeat(${cols}, minmax(150px, 1fr))` }}
  >
    <div className={`p-5 bg-slate-50/60 text-sm font-semibold text-slate-600 ${capitalize ? 'capitalize' : ''}`}>
      {label}
    </div>
    {children}
  </div>
);

const FeatureList = ({ features }) => (
  <ul className="space-y-2">
    {(features || []).slice(0, 4).map((f) => (
      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
        <Check className="h-4 w-4 text-[#FF4E50] mt-0.5 flex-shrink-0" />
        <span>{f}</span>
      </li>
    ))}
  </ul>
);

const ProductCompare = () => {
  const { data: products, loading } = useProducts();
  const [selected, setSelected] = useState([]);

  // Auto-select first 2 when products load
  useEffect(() => {
    if (products && products.length >= 2 && selected.length === 0) {
      setSelected([products[0].id, products[1].id]);
    }
  }, [products, selected.length]);

  const toggle = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 3) return [s[1], s[2], id];
      return [...s, id];
    });
  };

  const chosen = selected.map((id) => (products || []).find((p) => p.id === id)).filter(Boolean);
  const allSpecKeys = Array.from(new Set(chosen.flatMap((p) => Object.keys(p.specs || {}))));

  return (
    <section className="py-28 bg-slate-50 relative" data-testid="product-compare-section">
      <div className="container-pn">
        {/* Header */}
        <div className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3 inline-flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5" /> Side-by-side
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight">
            Compare our <span className="text-[#0A2A63]">spectrophotometers</span>
          </h2>
          <p className="mt-4 text-slate-600">Select up to 3 products to compare specs in real-time.</p>
        </div>

        <ProductChips products={products} loading={loading} selected={selected} onToggle={toggle} />

        <p className="sm:hidden text-xs text-slate-400 mb-3 flex items-center gap-1.5 font-medium">
          <span className="inline-block animate-pulse">←</span> Swipe the table sideways to see all specs
        </p>

        {/* Compare grid */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-x-auto">
          <div style={{ minWidth: 140 + Math.max(1, chosen.length) * 180 }}>
          <div
            className="grid border-b border-slate-100"
            style={{ gridTemplateColumns: `140px repeat(${Math.max(1, chosen.length)}, minmax(150px, 1fr))` }}
          >
            <div className="p-6 bg-slate-50">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Product</p>
            </div>
            {chosen.length === 0 && (
              <div className="p-6 text-slate-400 text-sm">Select products above to compare</div>
            )}
            {chosen.map((p) => (
              <ProductHeaderCell key={p.id} p={p} onRemove={() => toggle(p.id)} />
            ))}
          </div>

          {/* Spec rows */}
          {chosen.length > 0 && (
            <div className="divide-y divide-slate-100">
              {BASE_KEYS.map((key) => (
                <CompareRow key={key} label={humanize(key)} cols={chosen.length} capitalize>
                  {chosen.map((p) => (
                    <div key={p.id} className="p-5 border-l border-slate-100 text-[#050F2E] font-bold">
                      {p[key]}
                    </div>
                  ))}
                </CompareRow>
              ))}
              {allSpecKeys.map((k) => (
                <CompareRow key={k} label={k} cols={chosen.length}>
                  {chosen.map((p) => (
                    <div key={p.id} className="p-5 border-l border-slate-100 text-[#050F2E]">
                      {p.specs?.[k] || <span className="text-slate-300">—</span>}
                    </div>
                  ))}
                </CompareRow>
              ))}
              <CompareRow label="Key features" cols={chosen.length} alignStart>
                {chosen.map((p) => (
                  <div key={p.id} className="p-5 border-l border-slate-100">
                    <FeatureList features={p.features} />
                  </div>
                ))}
              </CompareRow>
            </div>
          )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductCompare;
